import { loadSession, type ClientSocket } from './game-store';
import { Device, type types as MsTypes } from 'mediasoup-client';
import type { IceServerConfig, IceTransportPolicy } from '@juegazo/shared';

export interface VoiceManagerOptions {
  socket: ClientSocket;
}

export interface PeerChangeInfo {
  playerId: string;
  enabled: boolean;
}

export interface VoiceError {
  code: string;
  message: string;
}

export type VoiceErrorHandler = (err: VoiceError) => void;
export type PeerChangeHandler = (info: PeerChangeInfo) => void;

interface TransportOpts {
  ok: true;
  transportId: string;
  iceParameters: MsTypes.IceParameters;
  iceCandidates: MsTypes.IceCandidate[];
  dtlsParameters: MsTypes.DtlsParameters;
  /**
   * Configuración ICE entregada por el server (S15). Es opcional para
   * tolerar servers viejos que no la envían.
   */
  iceServers?: IceServerConfig[];
  iceTransportPolicy?: IceTransportPolicy;
}

interface ConsumerOpts {
  ok: true;
  consumerId: string;
  producerId: string;
  kind: 'audio' | 'video';
  rtpParameters: MsTypes.RtpParameters;
}

interface RtpCapsAck {
  ok: true;
  rtpCapabilities: MsTypes.RtpCapabilities;
}

interface ErrAck {
  ok: false;
  error: string;
}

/**
 * Fallback: si el server no entrega configuración ICE (server viejo o el
 * ack llegó sin ella), se usa el STUN público por defecto. La fuente de
 * verdad es el server (`buildIceConfig`): el cliente NO decide.
 */
const DEFAULT_ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
];

export class VoiceManager {
  private readonly socket: ClientSocket;
  private device: Device | null = null;
  private sendTransport: MsTypes.Transport | null = null;
  private recvTransport: MsTypes.Transport | null = null;
  private producer: MsTypes.Producer | null = null;
  private consumers = new Map<string, MsTypes.Consumer>();
  private remoteAudios = new Map<string, HTMLAudioElement>();
  private enabled = false;
  private muted = true;
  private localStream: MediaStream | null = null;
  private peerHandlers = new Set<PeerChangeHandler>();
  private errorHandlers = new Set<VoiceErrorHandler>();
  private stateHandlers = new Set<() => void>();
  private socketHandlersBound = false;
  private startingPromise: Promise<void> | null = null;
  /**
   * Contador de generación del arranque. Cada teardown (`stop()`, `destroy()`,
   * desconexión) lo incrementa: así un `start()` en vuelo detecta que fue
   * cancelado y NO deja recursos vivos (micrófono prendido, transports
   * abiertos) al retomar el control.
   */
  private startGeneration = 0;
  /** Evita que dos teardowns se pisen (el `stop()` + `destroy()` de app-root). */
  private teardownInProgress = false;
  private wasEnabled = false;
  private autoStartOnMount = false;
  /** Ids de los producers propios, para no consumirlos (evita el eco). */
  private readonly localProducerIds = new Set<string>();
  /**
   * Configuración ICE vigente (S15). La entrega el server en el ack de
   * `voice:create-transport`; hasta entonces se usa el STUN por defecto.
   */
  private iceServers: RTCIceServer[] = DEFAULT_ICE_SERVERS;
  private iceTransportPolicy: RTCIceTransportPolicy = 'all';

  constructor(opts: VoiceManagerOptions) {
    this.socket = opts.socket;
    this.bindSocketHandlers();
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  wasEnabledBeforeDisconnect(): boolean {
    return this.wasEnabled;
  }

  /**
   * Marca que el siguiente render de un <jgz-voice-controls> debe activar el
   * micrófono automáticamente. Pensado para que el dueño del ciclo de vida
   * (app-root) lo invoque al crear el manager; el componente lo consume una
   * sola vez cuando monta. Un cambio de vista posterior (lobby → play) no
   * vuelve a disparar el auto-start porque la flag ya fue consumida.
   */
  enableOnMount(): void {
    this.autoStartOnMount = true;
  }

  /**
   * Consumir la señal de auto-start. Devuelve true solo si el manager está
   * marcado y todavía no está habilitado (evita un segundo start cuando la
   * reconexión por socket ya lo habilitó). Tras consumir, resetea la flag.
   */
  consumeAutoStartOnMount(): boolean {
    if (this.autoStartOnMount && !this.enabled) {
      this.autoStartOnMount = false;
      return true;
    }
    this.autoStartOnMount = false;
    return false;
  }

  isMicActive(): boolean {
    return this.enabled && !this.muted;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.producer) {
      if (muted) {
        this.producer.pause();
      } else {
        this.producer.resume();
      }
    }
    for (const audio of this.remoteAudios.values()) {
      audio.muted = muted;
    }
    this.notifyStateChange();
  }

  onPeerChange(handler: PeerChangeHandler): () => void {
    this.peerHandlers.add(handler);
    return () => this.peerHandlers.delete(handler);
  }

  onError(handler: VoiceErrorHandler): () => void {
    this.errorHandlers.add(handler);
    return () => this.errorHandlers.delete(handler);
  }

  onStateChange(handler: () => void): () => void {
    this.stateHandlers.add(handler);
    return () => this.stateHandlers.delete(handler);
  }

  async start(): Promise<void> {
    if (this.enabled) return this.startingPromise ?? Promise.resolve();
    this.enabled = true;
    this.muted = false;
    this.wasEnabled = true;
    this.notifyStateChange();
    this.socket.emit('voice:enable');
    const generation = this.startGeneration;
    this.startingPromise = this.startMediasoup(generation)
      .catch((err) => {
        // Un start cancelado (stop/destroy/desconexión) no es un error que
        // haya que mostrarle al usuario: el pipeline ya fue tearing down.
        if (generation !== this.startGeneration) return;
        this.emitError('start_failed', err instanceof Error ? err.message : String(err));
        this.enabled = false;
        this.wasEnabled = false;
        this.muted = true;
        this.socket.emit('voice:disable');
        this.notifyStateChange();
      })
      .finally(() => {
        // La promesa vieja no se reusa: un start posterior arranca de cero.
        if (generation === this.startGeneration) this.startingPromise = null;
      });
    return this.startingPromise;
  }

  async stop(): Promise<void> {
    // Invalida cualquier `start()` en vuelo antes de tearing down: su
    // continuación no debe volver a crear device/transports/producer.
    this.startGeneration++;
    if (!this.enabled) return;
    this.enabled = false;
    this.wasEnabled = false;
    this.notifyStateChange();
    this.socket.emit('voice:disable');
    await this.teardown();
  }

  /**
   * Construye el pipeline de mediasoup. Después de cada `await` se verifica
   * que la generación no haya cambiado: si el start fue cancelado
   * (`stop()`/`destroy()`/desconexión) se abandona sin dejar recursos vivos.
   */
  private async startMediasoup(generation: number): Promise<void> {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    // El teardown pudo correr mientras esperábamos el micrófono: este stream
    // nunca se asignó, hay que soltarlo acá.
    if (generation !== this.startGeneration) {
      this.stopStreamTracks(stream);
      return;
    }
    this.localStream = stream;
    const audioTrack = stream.getAudioTracks()[0];
    if (!audioTrack) throw new Error('no audio track in getUserMedia result');

    const capsAck = await this.requestRtpCapabilities();
    if (generation !== this.startGeneration) return;
    if (!capsAck.ok) throw new Error(`rtp-capabilities: ${capsAck.error}`);

    const device = new Device();
    await device.load({ routerRtpCapabilities: capsAck.rtpCapabilities });
    if (generation !== this.startGeneration) return;
    this.device = device;

    const sendOpts = await this.requestTransportOptions('send');
    if (generation !== this.startGeneration) return;
    if (!sendOpts.ok) throw new Error(`create-transport(send): ${sendOpts.error}`);
    const sendTransport = device.createSendTransport({
      id: sendOpts.transportId,
      iceParameters: sendOpts.iceParameters,
      iceCandidates: sendOpts.iceCandidates,
      dtlsParameters: sendOpts.dtlsParameters,
      iceServers: this.iceServers,
      iceTransportPolicy: this.iceTransportPolicy,
    });
    this.sendTransport = sendTransport;
    sendTransport.on('connect', ({ dtlsParameters }, callback) => {
      this.socket.emit('voice:connect-transport', {
        transportId: sendTransport.id,
        direction: 'send',
        dtlsParameters,
      });
      callback();
    });
    sendTransport.on('produce', ({ kind, rtpParameters }, callback, errback) => {
      this.socket.emit(
        'voice:produce',
        { kind: 'audio' as const, rtpParameters: rtpParameters as unknown },
        (resp: unknown) => {
          const r = resp as { ok: true; producerId: string } | ErrAck;
          if (!r.ok) {
            errback(new Error(r.error));
            return;
          }
          this.localProducerIds.add(r.producerId);
          callback({ id: r.producerId });
        },
      );
    });

    const producer = await sendTransport.produce({ track: audioTrack });
    if (generation !== this.startGeneration) {
      // El teardown ya corrió: este producer no lo va a usar nadie.
      producer.close();
      return;
    }
    this.producer = producer;
    if (this.muted) producer.pause();

    const recvOpts = await this.requestTransportOptions('recv');
    if (generation !== this.startGeneration) return;
    if (!recvOpts.ok) throw new Error(`create-transport(recv): ${recvOpts.error}`);
    const recvTransport = device.createRecvTransport({
      id: recvOpts.transportId,
      iceParameters: recvOpts.iceParameters,
      iceCandidates: recvOpts.iceCandidates,
      dtlsParameters: recvOpts.dtlsParameters,
      iceServers: this.iceServers,
      iceTransportPolicy: this.iceTransportPolicy,
    });
    this.recvTransport = recvTransport;
    recvTransport.on('connect', ({ dtlsParameters }, callback) => {
      this.socket.emit('voice:connect-transport', {
        transportId: recvTransport.id,
        direction: 'recv',
        dtlsParameters,
      });
      callback();
    });
  }

  private async consumeNewProducer(producerId: string, _playerId: string): Promise<void> {
    if (!this.recvTransport) return;
    if (this.consumers.has(producerId)) return;

    const capsAck = await this.requestRtpCapabilities();
    if (!capsAck.ok) {
      this.emitError('rtp_caps_failed', capsAck.error);
      return;
    }

    const ack = await this.requestConsume(producerId, capsAck.rtpCapabilities);
    if (!ack.ok) {
      this.emitError('consume_failed', ack.error);
      return;
    }

    const consumer = await this.recvTransport.consume({
      id: ack.consumerId,
      producerId: ack.producerId,
      kind: ack.kind,
      rtpParameters: ack.rtpParameters,
    });
    this.consumers.set(producerId, consumer);

    const stream = new MediaStream([consumer.track]);
    const audio = new Audio();
    audio.srcObject = stream;
    audio.autoplay = true;
    audio.muted = this.muted;
    document.body.appendChild(audio);
    this.remoteAudios.set(producerId, audio);

    this.socket.emit('voice:resume-consumer', { consumerId: consumer.id });
    void audio.play().catch((err) => {
      this.emitError('audio_play_failed', err instanceof Error ? err.message : String(err));
    });
  }

  /**
   * Camino único de teardown. Es idempotente: llamarla dos veces (por ejemplo
   * el `stop()` de app-root seguido del `destroy()`) no rompe nada ni deja
   * recursos vivos.
   */
  private async teardown(): Promise<void> {
    if (this.teardownInProgress) return;
    this.teardownInProgress = true;
    try {
      if (this.producer) {
        try {
          this.producer.close();
        } catch {
          // ignore
        }
        this.producer = null;
      }
      for (const c of this.consumers.values()) {
        try {
          c.close();
        } catch {
          // ignore
        }
      }
      this.consumers.clear();
      if (this.sendTransport) {
        try {
          this.sendTransport.close();
        } catch {
          // ignore
        }
        this.sendTransport = null;
      }
      if (this.recvTransport) {
        try {
          this.recvTransport.close();
        } catch {
          // ignore
        }
        this.recvTransport = null;
      }
      for (const a of this.remoteAudios.values()) {
        a.srcObject = null;
        a.remove();
      }
      this.remoteAudios.clear();
      if (this.localStream) {
        this.stopStreamTracks(this.localStream);
        this.localStream = null;
      }
      this.device = null;
    } finally {
      this.teardownInProgress = false;
    }
  }

  /** Cierra las pistas de audio de un stream (microfono local). */
  private stopStreamTracks(stream: MediaStream): void {
    for (const t of stream.getTracks()) t.stop();
  }

  private requestRtpCapabilities(): Promise<RtpCapsAck | ErrAck> {
    return new Promise((resolve) => {
      this.socket.emit('voice:request-rtp-capabilities', (resp: unknown) => {
        resolve(resp as RtpCapsAck | ErrAck);
      });
    });
  }

  private requestTransportOptions(direction: 'send' | 'recv'): Promise<TransportOpts | ErrAck> {
    return new Promise((resolve) => {
      this.socket.emit('voice:create-transport', { direction }, (resp: unknown) => {
        const opts = resp as TransportOpts | ErrAck;
        // S15: el server es la fuente de verdad de la configuración ICE. Si
        // la entrega, se adopta para este y para los transports siguientes.
        if (opts.ok) this.applyIceConfig(opts);
        resolve(opts);
      });
    });
  }

  /**
   * Adopta la configuración ICE entregada por el server (S15). Un server que
   * no la entrega deja intacta la configuración vigente.
   */
  private applyIceConfig(opts: TransportOpts): void {
    if (opts.iceServers && opts.iceServers.length > 0) {
      this.iceServers = opts.iceServers.map((s) => ({ ...s }));
    }
    if (opts.iceTransportPolicy) {
      this.iceTransportPolicy = opts.iceTransportPolicy;
    }
  }

  private requestConsume(
    producerId: string,
    rtpCapabilities: MsTypes.RtpCapabilities,
  ): Promise<ConsumerOpts | ErrAck> {
    return new Promise((resolve) => {
      this.socket.emit(
        'voice:consume',
        { producerId, rtpCapabilities: rtpCapabilities as unknown },
        (resp: unknown) => {
          resolve(resp as ConsumerOpts | ErrAck);
        },
      );
    });
  }

  private bindSocketHandlers(): void {
    if (this.socketHandlersBound) return;
    this.socketHandlersBound = true;
    const sock = this.socket as unknown as {
      on: (e: string, h: (...a: unknown[]) => void) => unknown;
    };
    sock.on('connect', this.handleConnect);
    sock.on('disconnect', this.handleDisconnect);
    sock.on('voice:peer-enabled', this.handlePeerEnabled as never);
    sock.on('voice:peer-disabled', this.handlePeerDisabled as never);
    sock.on('voice:new-producer', this.handleNewProducer as never);
    sock.on('voice:producer-closed', this.handleProducerClosed as never);
    sock.on('voice:error', this.handleVoiceError as never);
  }

  private unbindSocketHandlers(): void {
    if (!this.socketHandlersBound) return;
    this.socketHandlersBound = false;
    const sock = this.socket as unknown as {
      off: (e: string, h: (...a: unknown[]) => void) => unknown;
    };
    // Se desregistra SOLO el handler del VoiceManager: el socket es
    // compartido con el GameStore (auto-rejoin) y `off(event)` sin referencia
    // borraría también los listeners ajenos de ese socket.
    sock.off('connect', this.handleConnect);
    sock.off('disconnect', this.handleDisconnect);
    sock.off('voice:peer-enabled', this.handlePeerEnabled as never);
    sock.off('voice:peer-disabled', this.handlePeerDisabled as never);
    sock.off('voice:new-producer', this.handleNewProducer as never);
    sock.off('voice:producer-closed', this.handleProducerClosed as never);
    sock.off('voice:error', this.handleVoiceError as never);
  }

  private readonly handleConnect = (): void => {
    if (this.wasEnabled) {
      void this.start();
    }
  };

  private readonly handleDisconnect = (): void => {
    // The server destroys our VoicePeer on socket disconnect, so the local
    // mediasoup pipeline is broken too. Reset `enabled` (so the next start()
    // actually rebuilds instead of short-circuiting on its idempotency check)
    // and tear down the transports. Preserve `wasEnabled` so reconnect
    // restores the user's intent.
    // La generación se invalida: un `start()` en vuelo no debe resucitar el
    // pipeline después de la desconexión.
    this.startGeneration++;
    if (this.enabled) {
      this.enabled = false;
      void this.teardown();
    }
  };

  private readonly handlePeerEnabled = (info: { playerId: string }): void => {
    for (const h of this.peerHandlers) h({ playerId: info.playerId, enabled: true });
  };

  private readonly handlePeerDisabled = (info: { playerId: string }): void => {
    for (const h of this.peerHandlers) h({ playerId: info.playerId, enabled: false });
  };

  private readonly handleNewProducer = (info: { playerId: string; producerId: string }): void => {
    // Defensa en profundidad: el servidor ya excluye al producer del
    // broadcast, pero si un `voice:new-producer` propio llegara igual
    // (servidor viejo, otro emisor, replay) consumirlo significaría
    // escucharse a sí mismo con eco.
    if (this.isLocalPlayer(info.playerId)) return;
    if (info.producerId && this.localProducerIds.has(info.producerId)) return;
    void this.consumeNewProducer(info.producerId, info.playerId).catch((err) => {
      this.emitError('consume_setup_failed', err instanceof Error ? err.message : String(err));
    });
  };

  private readonly handleProducerClosed = (info: { producerId: string }): void => {
    const consumer = this.consumers.get(info.producerId);
    if (consumer) {
      try {
        consumer.close();
      } catch {
        // ignore
      }
      this.consumers.delete(info.producerId);
    }
    const audio = this.remoteAudios.get(info.producerId);
    if (audio) {
      audio.srcObject = null;
      audio.remove();
      this.remoteAudios.delete(info.producerId);
    }
  };

  private readonly handleVoiceError = (err: VoiceError): void => {
    for (const h of this.errorHandlers) h(err);
  };

  /**
   * Id del jugador local. El socket del cliente no replica `socket.data` del
   * servidor, así que la fuente confiable es la sesión guardada por el
   * GameStore (`loadSession`), con `socket.data.playerId` como atajo cuando
   * estuviese disponible.
   */
  private isLocalPlayer(playerId: string): boolean {
    if (!playerId) return false;
    const local = this.localPlayerId();
    return local !== null && local === playerId;
  }

  private localPlayerId(): string | null {
    const fromSocket = (this.socket as unknown as { data?: { playerId?: string } }).data?.playerId;
    if (fromSocket) return fromSocket;
    try {
      return loadSession().playerId ?? null;
    } catch {
      return null;
    }
  }

  private emitError(code: string, message: string): void {
    const err = { code, message };
    console.error(`[voice] ${code}: ${message}`);
    for (const h of this.errorHandlers) h(err);
  }

  private notifyStateChange(): void {
    for (const h of this.stateHandlers) h();
  }

  destroy(): void {
    // Invalida un `start()` en vuelo y pasa por el mismo camino de teardown
    // que `stop()` (idempotente).
    this.startGeneration++;
    this.unbindSocketHandlers();
    this.enabled = false;
    this.notifyStateChange();
    void this.teardown();
  }
}
